// Listens to global WebSocket events + custom app events and pushes them into the notification center.
// Mounted once inside TopBar so it lives for the whole session.
import { useEffect } from "react";
import useWebSocket from "@/hooks/useWebSocket";
import { useAuth } from "@/contexts/AuthContext";
import { pushNotification } from "@/hooks/useNotificationCenter";
import { playNotificationSound, vibrateNotification, getStoredSoundPref } from "@/lib/notificationSound";

const ORDER_STATUS_LABEL = {
  pending:   "تم استلام طلبك — بانتظار التأكيد",
  preparing: "المطعم يُحضّر طلبك",
  ready:     "طلبك جاهز — بانتظار سائق",
  assigned:  "تم إسناد سائق لطلبك",
  picked_up: "السائق في الطريق إليك",
  delivered: "تم تسليم طلبك ✓",
  cancelled: "تم إلغاء الطلب",
};

export default function NotificationListener() {
  const { user } = useAuth();

  // Sound-on-focus unlock — click anywhere to enable audio
  useEffect(() => {
    const unlock = () => { /* touching AudioContext via playNotificationSound(...) will resume it */ };
    window.addEventListener("click", unlock, { once: true });
    return () => window.removeEventListener("click", unlock);
  }, []);

  const notify = (payload, kind = "order") => {
    if (!user?.id) return;
    // Respect user's notification preferences (cached from Profile page).
    try {
      const raw = localStorage.getItem("talabya_notif_prefs_v1");
      const prefs = raw ? JSON.parse(raw) : null;
      const prefKey = kind === "wallet" ? "wallet_updates"
                    : kind === "promo"  ? "promos"
                    : kind === "chat"   ? "chat"
                    : "order_updates";
      if (prefs && prefs[prefKey] === false) return; // muted
    } catch { /* ignore */ }
    pushNotification(user.id, payload);
    const pref = getStoredSoundPref();
    if (pref !== "silent") {
      playNotificationSound(pref);
      vibrateNotification([200, 100, 200]);
    }
  };

  useWebSocket({
    onMessage: (msg) => {
      if (!user?.id || !msg?.event) return;

      // ORDER status changes — subscribed via order:{id} topics on tracking / customer pages
      if (msg.event === "order.status_changed" && msg.data) {
        const { status, order_code, order_id } = msg.data;
        const label = ORDER_STATUS_LABEL[status] || `تحديث الطلب: ${status}`;
        notify({
          title: label,
          body: order_code ? `طلب #${order_code}` : "",
          kind: "order",
          orderCode: order_code || null,
          url: order_id ? `/track/${order_id}` : null,
        });
        return;
      }
      if (msg.event === "order.created" && msg.data) {
        notify({
          title: "تم استلام طلبك",
          body: msg.data.order_code ? `طلب #${msg.data.order_code}` : "",
          kind: "order",
          orderCode: msg.data.order_code || null,
          url: msg.data.order_id ? `/track/${msg.data.order_id}` : null,
        });
        return;
      }

      // WALLET updates on user:{id}
      if (msg.event === "wallet.updated" && msg.topic?.startsWith("user:") && msg.data?.transaction) {
        const tx = msg.data.transaction;
        const positive = (tx.amount || 0) >= 0;
        const titleMap = {
          topup: "تم شحن محفظتك",
          card_redeem: "تم شحن المحفظة بكرت",
          deduct: "خصم من محفظتك",
          order_payment: "دفع من محفظتك",
          order_refund: "استرداد إلى محفظتك",
          loyalty_redeem: "استبدال نقاط ولاء",
        };
        notify({
          title: titleMap[tx.type] || (positive ? "إضافة إلى المحفظة" : "خصم من المحفظة"),
          body: `${positive ? "+" : ""}${(tx.amount || 0).toFixed(2)} د.ل • الرصيد: ${(msg.data.balance || 0).toFixed(2)} د.ل`,
          kind: "wallet",
          url: "/profile",
        }, "wallet");
        return;
      }

      // CHAT — new order message on order:{id}
      if (msg.event === "chat.message" && msg.data) {
        const { text, sender_role, sender_name, order_id, order_code } = msg.data;
        // Skip our own messages
        if (msg.data.sender_id === user.id) return;
        notify({
          title: `رسالة من ${sender_role === "driver" ? "السائق" : sender_role === "customer" ? "الزبون" : "الإدارة"} ${sender_name || ""}`.trim(),
          body: (text || "").slice(0, 140),
          kind: "info",
          orderCode: order_code || null,
          url: order_id ? `/track/${order_id}` : null,
        }, "chat");
        return;
      }

      // Driver — new available order
      if (msg.event === "driver.new_available_order" && msg.data) {
        notify({
          title: "طلب جديد جاهز للاستلام",
          body: msg.data.order_code ? `#${msg.data.order_code}` : "",
          kind: "order",
          orderCode: msg.data.order_code || null,
          url: "/driver",
        });
      }
    },
  });

  return null;
}
